import { Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { BLOG_CATEGORIES } from '../../../data/blog/blog-category.data';
import { Blog } from '../../../model/blog/blog.model';

/**
 * The home's blog box (owner 2026-10-11: the blog section redesigned as a box like the others): the app
 * boxes' panel (product-showcase.component.scss) with one post at a time, in the site's own colors. Left: the
 * post (its category, title, the first sentences of its excerpt, date, reading time) and Read Post. Right: its
 * cover. Under both, All Posts: each category's count and every post as its round icon in a sliding row. The box turns to the next post by itself,
 * and holds while the pointer or the keyboard is in it.
 */
@Component( {
  selector: 'app-blog-showcase',
  templateUrl: './blog-showcase.component.html',
  styleUrls: [
    '../product-showcase/product-showcase.component.scss',
    './blog-showcase.component.scss',
  ],
} )
export class BlogShowcaseComponent implements OnInit, OnChanges, OnDestroy {
  /** the latest posts, newest first */
  @Input() posts: Blog[] = [];
  /** the line over the post: the home's "From The Blog", a category's name on the blog page */
  @Input() eyebrow = 'From The Blog';
  /** the post's category over its title */
  @Input() showCategory = true;

  @ViewChild( 'deck' ) deck?: ElementRef<HTMLElement>;

  index = 0;

  private timer?: ReturnType<typeof setInterval>;
  private held = false;
  private static readonly TURN_MS = 9000;

  constructor( private zone: NgZone ) {}

  ngOnInit(): void {
    if ( typeof window === 'undefined' || window.matchMedia?.( '(prefers-reduced-motion: reduce)' ).matches ) {
      return;
    }
    this.zone.runOutsideAngular( () => {
      this.timer = setInterval( () => {
        if ( !this.held && this.posts.length > 1 ) {
          this.zone.run( () => this.show( ( this.index + 1 ) % this.posts.length ) );
        }
      }, BlogShowcaseComponent.TURN_MS );
    } );
  }

  /** a new list (the blog page's category filter) starts again from its newest post */
  ngOnChanges( changes: SimpleChanges ): void {
    if ( changes[ 'posts' ] && !changes[ 'posts' ].firstChange ) {
      this.show( 0 );
    }
  }

  ngOnDestroy(): void {
    clearInterval( this.timer );
  }

  get post(): Blog | undefined {
    return this.posts[ this.index ];
  }

  pick( i: number ): void {
    this.show( i );
  }

  /** a post in the row: bring it up into the box (a modified click keeps the link: a new tab) */
  bringUp( i: number, event: MouseEvent ): void {
    if ( event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0 ) return;
    event.preventDefault();
    this.show( i );
  }

  /** show a post, and slide the row (never the page) so its icon sits in the middle */
  private show( i: number ): void {
    this.index = i;
    setTimeout( () => {
      const deck = this.deck?.nativeElement;
      const thumb = deck?.children[ i ] as HTMLElement | undefined;
      if ( !deck || !thumb ) return;
      const left = thumb.offsetLeft - deck.offsetLeft - ( deck.clientWidth - thumb.offsetWidth ) / 2;
      deck.scrollTo( { left: Math.max( 0, left ), behavior: 'smooth' } );
    } );
  }

  hold( on: boolean ): void {
    this.held = on;
  }

  /** the left side's text: whole sentences of the excerpt, as many as fill its four lines (never cut) */
  get story(): string {
    const text = this.post?.excerpt || '';
    const sentences = text.match( /[^.!?]+[.!?]+(\s|$)/g ) || [];
    let out = '';
    for ( const s of sentences ) {
      const next = ( out + s ).trim();
      if ( out && next.length > 200 ) {
        break;
      }
      out = next + ' ';
    }
    return out.trim() || text;
  }

  /** each category with how many of the posts it has, in the blog's category order */
  get categories(): { label: string; count: number }[] {
    return BLOG_CATEGORIES
      .map( c => ( { label: c.label, count: this.posts.filter( p => p.category === c.key ).length } ) )
      .filter( c => c.count > 0 );
  }

  trackPost( _: number, post: Blog ): string {
    return post.key;
  }
}
